"use client";

import React, { useState, useRef, useEffect } from "react";
import { useField } from "formik";
import {
  X,
  AlertCircle,
  Plus,
  Video,
} from "lucide-react";

interface MultiImageUploadFieldProps {
  label: string;
  name: string;
  required?: boolean;
  maxSize?: number; // in MB
  accept?: string;
  variant?: "light" | "dark";
}

export const MultiImageUploadField: React.FC<MultiImageUploadFieldProps> = ({
  label,
  name,
  required = false,
  maxSize = 10,
  accept = "image/*",
  variant = "light",
}) => {
  const [field, meta, helpers] = useField(name);
  const [previews, setPreviews] = useState<{ url: string; type: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isError = meta.touched && meta.error;

  const currentValues = Array.isArray(field.value) ? field.value : [];

  useEffect(() => {
    const loadPreviews = async () => {
      const newPreviews = await Promise.all(
        currentValues.map(async (val: any) => {
          const isVideo =
            (val instanceof File && val.type.startsWith("video/")) ||
            (typeof val === "string" &&
              (val.endsWith(".mp4") ||
                val.endsWith(".webm") ||
                val.endsWith(".mov")));

          if (typeof val === "string") return { url: val, type: isVideo ? "video" : "image" };
          
          if (val instanceof File) {
            if (isVideo) {
              return { url: URL.createObjectURL(val), type: "video" };
            }
            return new Promise<{ url: string; type: string }>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () =>
                resolve({
                  url: reader.result as string,
                  type: "image",
                });
              reader.readAsDataURL(val);
            });
          }
          return null;
        }),
      );
      setPreviews(newPreviews.filter((p): p is { url: string; type: string } => p !== null));
    };
    loadPreviews();
  }, [field.value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const validFiles = files.filter((file) => {
        if (file.size > maxSize * 1024 * 1024) {
          alert(`File ${file.name} is too large. Max size is ${maxSize}MB`);
          return false;
        }
        return true;
      });
      helpers.setValue([...currentValues, ...validFiles]);
      helpers.setTouched(true);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemove = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = [...currentValues];
    newValues.splice(index, 1);
    helpers.setValue(newValues);
  };

  return (
    <div className="space-y-4 flex flex-col">
      <div className="flex items-center justify-between">
        <label
          className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
            variant === "dark" ? "text-text-3" : "text-text-3"
          }`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {isError && (
          <div className="flex items-center gap-1 text-[10px] text-red-500 font-bold">
            <AlertCircle size={10} />
            <span>{meta.error}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        {previews.map((item, idx) => (
          <div
            key={idx}
            onMouseEnter={(e) => e.currentTarget.querySelector("video")?.play()}
            onMouseLeave={(e) => e.currentTarget.querySelector("video")?.pause()}
            className={`w-28 h-28 sm:w-32 sm:h-32 shrink-0 relative group rounded-xl border overflow-hidden shadow-sm cursor-pointer ${
              variant === "dark"
                ? "border-border-1 bg-black-2"
                : "border-border-1 bg-black-2"
            }`}
          >
            {item.type === "video" ? (
              <div className="w-full h-full relative group-hover:scale-105 transition-transform duration-300">
                <video
                  src={item.url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  preload="auto"
                  onContextMenu={(e) => e.preventDefault()}
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
                  <div className="p-3 bg-black/40 backdrop-blur-sm rounded-full text-white border border-white/20">
                    <Video size={20} />
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={item.url}
                alt={`Preview ${idx}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
            )}
            
            {/* Type Badge */}
            <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/60 backdrop-blur-md rounded text-[7px] font-bold text-white uppercase tracking-widest pointer-events-none">
              {item.type}
            </div>

            <button
              type="button"
              onClick={(e) => handleRemove(idx, e)}
              className="absolute top-2 right-2 p-1.5 bg-white/10 hover:bg-red-500 backdrop-blur-md border border-white/20 rounded-full text-white shadow-sm transition-all z-10"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className={`w-28 h-28 sm:w-32 sm:h-32 shrink-0 rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-3 p-4 ${
            isError
              ? "border-red-200 bg-red-50/30 text-red-400"
              : variant === "dark"
                ? "border-border-1 hover:border-gold-dim hover:bg-black-3 text-text-3 hover:text-gold-dim"
                : "border-border-1 hover:border-gold-dim hover:bg-black-2 text-text-3 hover:text-gold-dim"
          }`}
        >
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center group-hover:bg-gold-dim/10 transition-colors ${
              variant === "dark" ? "bg-black-2" : "bg-black-2"
            }`}
          >
            <Plus size={20} />
          </div>
          <div className="text-center">
            <span className="text-[9px] font-bold uppercase tracking-wider block">
              Add Media
            </span>
            <span className="text-[7px] opacity-60 uppercase tracking-tight block mt-0.5">
              Max {maxSize}MB
            </span>
          </div>
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};
